import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { supabase } from '../../../lib/supabaseClient';
import { edgeFunctionUrl, EdgeFunctions } from '../../../lib/edgeFunctions';

// Initialize Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');

interface TipModalProps {
  isOpen: boolean;
  onClose: () => void;
  appointmentId: string;
  serviceTotal: number;
  staffName: string;
  onSuccess: (tipData: { amount: number; tip_percentage: number | null }) => void;
  paymentMethodId?: string | null; // For restaurant-style auto-charge
  paymentSource?: string | null; // 'terminal' or 'checkout'
}

// Payment form component (Step 2)
const TipPaymentForm: React.FC<{
  appointmentId: string;
  tipPercentage: number | null;
  tipAmount: number;
  staffName: string;
  onSuccess: (tipData: { amount: number; tip_percentage: number | null }) => void;
  onBack: () => void;
}> = ({ appointmentId, tipPercentage, tipAmount, staffName, onSuccess, onBack }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);

    try {
      // Confirm payment
      const { error: submitError } = await elements.submit();
      if (submitError) {
        throw new Error(submitError.message);
      }

      const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: window.location.href, // Not used, but required
        },
        redirect: 'if_required', // Stay on page
      });

      if (confirmError) {
        throw new Error(confirmError.message);
      }

      if (paymentIntent && paymentIntent.status === 'succeeded') {
        // Confirm payment on backend
        const { data: { session } } = await supabase.auth.getSession();

        const response = await fetch(edgeFunctionUrl(EdgeFunctions.CONFIRM_TIP_PAYMENT), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token || ''}`,
            'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY || '',
          },
          body: JSON.stringify({
            payment_intent_id: paymentIntent.id,
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Failed to confirm tip payment');
        }

        // Success! Pass tip data to parent
        onSuccess({ amount: tipAmount, tip_percentage: tipPercentage });
      } else {
        throw new Error('Payment was not successful');
      }
    } catch (error: any) {
      console.error('Tip payment error:', error);
      setErrorMessage(error.message || 'Payment failed. Please try again.');
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="text-center mb-4">
        <h3 className="text-lg font-semibold text-gray-800">Complete Tip Payment</h3>
        <p className="text-gray-600 mt-1">Tip for {staffName}</p>
        <p className="text-2xl font-bold text-green-600 mt-2">
          ${tipAmount.toFixed(2)}
          {tipPercentage && <span className="text-sm text-gray-500 ml-2">({tipPercentage}%)</span>}
        </p>
      </div>

      <div className="bg-gray-50 rounded-lg p-4">
        <PaymentElement />
      </div>

      {errorMessage && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-800">{errorMessage}</p>
        </div>
      )}

      <div className="flex gap-3">
        <button
          type="button"
          onClick={onBack}
          disabled={isProcessing}
          className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors disabled:opacity-50"
        >
          Back
        </button>
        <button
          type="submit"
          disabled={!stripe || isProcessing}
          className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isProcessing ? (
            <span className="flex items-center justify-center gap-2">
              <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
              Processing...
            </span>
          ) : (
            'Confirm Tip'
          )}
        </button>
      </div>
    </form>
  );
};

// Main TipModal component
const TipModal: React.FC<TipModalProps> = ({
  isOpen,
  onClose,
  appointmentId,
  serviceTotal,
  staffName,
  onSuccess,
  paymentMethodId,
  paymentSource,
}) => {
  const [step, setStep] = useState<'select' | 'payment'>('select');
  const [selectedPercentage, setSelectedPercentage] = useState<number | null>(null);
  const [customPercentage, setCustomPercentage] = useState<string>('');
  const [tipAmount, setTipAmount] = useState<number>(0);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Preset percentages
  const presets = [15, 18, 20, 25];

  // Calculate tip amount when percentage changes
  useEffect(() => {
    if (selectedPercentage !== null) {
      const amount = (serviceTotal * selectedPercentage) / 100;
      setTipAmount(Math.round(amount * 100) / 100);
    }
  }, [selectedPercentage, serviceTotal]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setStep('select');
      setSelectedPercentage(null);
      setCustomPercentage('');
      setTipAmount(0);
      setClientSecret(null);
      setErrorMessage(null);
      setIsCreating(false);
    }
  }, [isOpen]);

  const handlePresetClick = (percentage: number) => {
    setSelectedPercentage(percentage);
    setCustomPercentage('');
  };

  const handleCustomChange = (value: string) => {
    setCustomPercentage(value);
    const percentage = parseFloat(value);
    if (!isNaN(percentage) && percentage > 0) {
      setSelectedPercentage(percentage);
    } else {
      setSelectedPercentage(null);
      setTipAmount(0);
    }
  };

  const handleContinue = async () => {
    if (selectedPercentage === null || tipAmount <= 0) {
      setErrorMessage('Please select a tip amount');
      return;
    }

    setIsCreating(true);
    setErrorMessage(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();

      // Check if we should try auto-charge (restaurant style)
      const shouldAutoCharge = !!paymentMethodId && paymentSource === 'terminal';

      const response = await fetch(edgeFunctionUrl(EdgeFunctions.CREATE_TIP_PAYMENT), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token || ''}`,
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY || '',
        },
        body: JSON.stringify({
          appointment_id: appointmentId,
          tip_percentage: selectedPercentage,
          auto_charge: shouldAutoCharge,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create tip payment');
      }

      // If auto-charged successfully, close modal immediately
      if (data.autoCharged && data.paymentStatus === 'succeeded') {
        handlePaymentSuccess({ amount: tipAmount, tip_percentage: selectedPercentage });
      } else if (data.clientSecret) {
        // Show payment form (only if we have a client secret)
        setClientSecret(data.clientSecret);
        setStep('payment');
      } else {
        // No client secret and not auto-charged - something went wrong
        throw new Error('Payment could not be processed. Please try again.');
      }
    } catch (error: any) {
      console.error('Error creating tip payment:', error);
      setErrorMessage(error.message || 'Failed to create payment. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  const handlePaymentSuccess = (tipData: { amount: number; tip_percentage: number | null }) => {
    onSuccess(tipData);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-800">Add Tip</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl"
          >
            &times;
          </button>
        </div>

        <div className="px-6 py-4">
          {step === 'select' ? (
            <div className="space-y-4">
              <div className="text-center">
                <p className="text-gray-600">Add a tip for {staffName}</p>
                <p className="text-lg font-semibold text-gray-800 mt-2">
                  Service Total: ${serviceTotal.toFixed(2)}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Tip Percentage
                </label>
                <div className="grid grid-cols-4 gap-2">
                  {presets.map((percentage) => (
                    <button
                      key={percentage}
                      type="button"
                      onClick={() => handlePresetClick(percentage)}
                      className={`p-3 rounded-lg border-2 transition-all ${
                        selectedPercentage === percentage && !customPercentage
                          ? 'border-green-600 bg-green-50 text-green-700'
                          : 'border-gray-300 hover:border-green-400 text-gray-700'
                      }`}
                    >
                      <div className="font-semibold">{percentage}%</div>
                      <div className="text-xs mt-1">
                        ${((serviceTotal * percentage) / 100).toFixed(2)}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Or enter custom percentage
                </label>
                <div className="relative">
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={customPercentage}
                    onChange={(e) => handleCustomChange(e.target.value)}
                    placeholder="Enter percentage"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 pr-8"
                  />
                  <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                    %
                  </span>
                </div>
              </div>

              {tipAmount > 0 && (
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-sm text-gray-600">Tip Amount</p>
                  <p className="text-2xl font-bold text-green-700">
                    ${tipAmount.toFixed(2)}
                  </p>
                </div>
              )}

              {errorMessage && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-800">{errorMessage}</p>
                </div>
              )}

              {/* Restaurant-style tip indicator */}
              {paymentMethodId && paymentSource === 'terminal' && tipAmount > 0 && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                  <p className="text-sm text-blue-800 text-center">
                    💳 Will charge to same card used for payment (restaurant style)
                  </p>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
                >
                  No Tip
                </button>
                <button
                  type="button"
                  onClick={handleContinue}
                  disabled={selectedPercentage === null || tipAmount <= 0 || isCreating}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isCreating
                    ? (paymentMethodId && paymentSource === 'terminal' ? 'Processing...' : 'Creating...')
                    : (paymentMethodId && paymentSource === 'terminal' ? 'Confirm & Charge' : 'Continue')}
                </button>
              </div>
            </div>
          ) : (
            clientSecret && (
              <Elements stripe={stripePromise} options={{ clientSecret }}>
                <TipPaymentForm
                  appointmentId={appointmentId}
                  tipPercentage={selectedPercentage}
                  tipAmount={tipAmount}
                  staffName={staffName}
                  onSuccess={handlePaymentSuccess}
                  onBack={() => setStep('select')}
                />
              </Elements>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default TipModal;
